/**
 * Utility to automatically hide the async-messaging chat icon or ask ai widget when modals/drawers are open.
 * This prevents the chat icon from appearing on top of modal popups.
 *
 * Usage: Call `initAsyncMessagingVisibilityManager()` once when the app initializes.
 * The utility uses a MutationObserver to detect when modal/drawer elements are added to the DOM
 * and automatically hides/shows the chat icon accordingly.
 */

const CHAT_ICON_SELECTORS = ['async-messaging-custom-chat-icon-holder', 'askai-widget'];

// Selectors to detect modal/drawer overlays
const MODAL_SELECTORS = [
  '[data-radix-portal]', // Radix UI portals (used by @abyss/web/ui/Drawer, Modal, etc.)
  '.abyss-modal-content-container',
  '.abyss-modal-overlay',
  '[role="dialog"]',
  '[aria-modal="true"]',
  '[data-radix-popper-content-wrapper]',
];

/**
 * Checks if a dialog element is actually visible/active
 * This handles cases where role="dialog" exists but modal isn't truly open
 */
const isDialogVisible = (element: Element): boolean => {
  // If aria-hidden="true", dialog is not visible
  if (element.getAttribute('aria-hidden') === 'true') {
    return false;
  }

  // Check if element is actually visible via CSS
  if (element instanceof HTMLElement) {
    const styles = window.getComputedStyle(element);
    if (
      styles.display === 'none' ||
      styles.visibility === 'hidden' ||
      styles.opacity === '0'
    ) {
      return false;
    }
  }

  return true;
};

let observer: MutationObserver | null = null;
let isInitialized = false;
let pendingUpdate: number | null = null;

/**
 * Checks if any modal/drawer is currently open and visible in the DOM
 */
const isModalOpen = (): boolean => {
  // Early exit: check for most common modal types first
  const radixPortal = document.querySelector('[data-radix-portal]');
  if (radixPortal) return true;

  // Check other selectors
  for (const selector of MODAL_SELECTORS) {
    if (selector === '[data-radix-portal]') continue; // Already checked

    const elements = document.querySelectorAll(selector);
    if (elements.length === 0) continue;

    for (const element of elements) {
      // For role="dialog" elements, verify they're actually visible
      if (selector === '[role="dialog"]') {
        if (isDialogVisible(element)) return true;
      } else {
        return true;
      }
    }
  }

  return false;
};

/**
 * Gets all chat icon elements
 */
const getChatIconElements = (): HTMLElement[] => {
  const elements: HTMLElement[] = [];
  CHAT_ICON_SELECTORS.forEach((id) => {
    const element = document.getElementById(id);
    if (element) {
      elements.push(element);
    }
  });
  return elements;
};

/**
 * Updates the visibility of the chat icon based on modal state
 */
const updateChatIconVisibility = (): void => {
  const chatIcons = getChatIconElements();
  if (chatIcons.length === 0) return; // Early exit if no chat icons

  const shouldHide = isModalOpen();

  // Batch DOM updates
  chatIcons.forEach((icon) => {
    const newDisplay = shouldHide ? 'none' : '';
    // Only update if value is different to avoid unnecessary reflows
    if (icon.style.display !== newDisplay) {
      icon.style.display = newDisplay;
    }
  });
};

/**
 * Debounced version of updateChatIconVisibility
 */
const debouncedUpdate = (): void => {
  if (pendingUpdate !== null) {
    cancelAnimationFrame(pendingUpdate);
  }
  pendingUpdate = requestAnimationFrame(() => {
    pendingUpdate = null;
    updateChatIconVisibility();
  });
};

/**
 * Initializes the async messaging visibility manager.
 * This sets up a MutationObserver that watches for DOM changes
 * and automatically hides/shows the chat icon when modals open/close.
 *
 * @returns A cleanup function to disconnect the observer
 */
export const initAsyncMessagingVisibilityManager = (): (() => void) => {
  // Prevent multiple initializations
  if (isInitialized || typeof window === 'undefined') {
    return () => {};
  }

  isInitialized = true;

  // Create a MutationObserver to watch for DOM changes
  observer = new MutationObserver((mutations) => {
    const isModalRelatedNode = (node: Node): boolean => {
      if (!(node instanceof HTMLElement)) {
        return false;
      }

      return (
        MODAL_SELECTORS.some(
          (selector) =>
            node.matches?.(selector) || node.querySelector?.(selector)
        ) || node.hasAttribute?.('data-radix-portal')
      );
    };

    const isChatIconNode = (node: Node): boolean => {
      if (!(node instanceof HTMLElement)) {
        return false;
      }

      if (CHAT_ICON_SELECTORS.some((id) => node.id === id)) {
        return true;
      }

      if (!node.hasChildNodes()) {
        return false;
      }

      return CHAT_ICON_SELECTORS.some((id) => node.querySelector?.(`#${id}`));
    };

    const hasModalRelatedNodes = (nodes: NodeList): boolean =>
      Array.from(nodes).some(isModalRelatedNode);

    const hasChatIconNodes = (nodes: NodeList): boolean =>
      Array.from(nodes).some(isChatIconNode);

    // Check if any mutation involves modal-related elements
    const hasRelevantMutation = mutations.some((mutation) => {
      // Check for attribute changes on dialog elements (aria-hidden, aria-modal only)
      if (
        mutation.type === 'attributes' &&
        mutation.target instanceof HTMLElement
      ) {
        const target = mutation.target;
        // Only care about changes to dialog/modal elements, not all elements
        if (
          target.matches?.('[role="dialog"]') ||
          target.matches?.('[aria-modal]') ||
          target.matches?.('[aria-hidden]')
        ) {
          return true;
        }
      }

      if (mutation.addedNodes.length > 0) {
        return (
          hasModalRelatedNodes(mutation.addedNodes) ||
          hasChatIconNodes(mutation.addedNodes)
        );
      }

      if (mutation.removedNodes.length > 0) {
        return (
          hasModalRelatedNodes(mutation.removedNodes) ||
          hasChatIconNodes(mutation.removedNodes)
        );
      }

      return false;
    });

    if (hasRelevantMutation) {
      if (pendingUpdate !== null) {
        clearTimeout(pendingUpdate as unknown as number);
      }
      pendingUpdate = setTimeout(() => {
        pendingUpdate = null;
        debouncedUpdate();
      }, 400) as unknown as number;
    }
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true, // Watch for attribute changes (aria-hidden, aria-modal)
    attributeFilter: ['aria-hidden', 'aria-modal'],
    attributeOldValue: false, // Don't store old values to save memory
  });

  updateChatIconVisibility();

  // Return cleanup function
  return () => {
    if (observer) {
      observer.disconnect();
      observer = null;
    }

    if (pendingUpdate !== null) {
      clearTimeout(pendingUpdate as unknown as number);
      cancelAnimationFrame(pendingUpdate);
      pendingUpdate = null;
    }

    isInitialized = false;

    // Restore chat icon visibility on cleanup
    const chatIcons = getChatIconElements();
    chatIcons.forEach((icon) => {
      icon.style.display = '';
    });
  };
};

/**
 * Manually trigger a visibility check.
 * Useful when you know a modal has opened/closed but the observer might not have caught it.
 */
export const checkAsyncMessagingVisibility = (): void => {
  updateChatIconVisibility();
};

/**
 * Force hide the chat icon temporarily.
 * Returns a function to restore visibility.
 */
export const forceHideAsyncMessaging = (): (() => void) => {
  const chatIcons = getChatIconElements();
  chatIcons.forEach((icon) => {
    icon.style.display = 'none';
  });

  return () => {
    // Only restore if no modals are open
    if (!isModalOpen()) {
      chatIcons.forEach((icon) => {
        icon.style.display = '';
      });
    }
  };
};

export default initAsyncMessagingVisibilityManager;
